/**
 * When to tell a person their session is over, and when to quietly extend it.
 *
 * The session token used to last minutes (a day since 2026-10-06). In the original Device Lab, running out of it did not *say*
 * anything: every panel simply started failing on its own schedule — a red line here, a stuck spinner
 * there, video that stopped — and the way to find out was to guess and re-login. The owner read that
 * screen as "щось ти зламав капітально", which is the correct reading of it.
 *
 * Kept as pure functions so the rules are testable without a browser: the mistake to avoid is a dialog
 * that fires when it should not — on the sign-in screen, or for a service token that has no expiry.
 */

import type { AuthSource } from './session'

/** What the workspace should do about the session, right now. */
export type SessionVerdict = { state: 'ok' } | { state: 'expired' }

export interface SessionFacts {
  /** epoch ms of the last real interaction — a tap, a key, a scroll, a gesture on a phone */
  lastActivityAt?: number
  /** epoch ms when the token stops being accepted, or null if we were not told */
  expiresAt: number | null
  /** 'password' = signed in here; 'manual' = a service token typed in by hand; null = nobody */
  source: AuthSource | null
  /** a request has come back 401 — the server's opinion, which beats our clock */
  refused?: boolean
  /** epoch ms when the current token was issued (sign-in or the last renewal); unknown for a session
   *  stored before this field existed */
  issuedAt?: number
}

//: Ask a few seconds early. A token that expires while a request is in flight produces exactly the
//: scattered failures this dialog exists to replace.
export const EXPIRY_GRACE_MS = 5_000

export function sessionVerdict(facts: SessionFacts, now: number): SessionVerdict {
  // Nothing to say when nobody is signed in — the sign-in screen already explains itself.
  if (facts.source === null) return { state: 'ok' }
  // A manual/service token is the admin path: no expiry we know about. A 401 on one means the key is
  // wrong, not that a session ran out.
  if (facts.source === 'manual') return { state: 'ok' }
  if (facts.refused) return { state: 'expired' }
  if (facts.expiresAt !== null && now >= facts.expiresAt - EXPIRY_GRACE_MS) return { state: 'expired' }
  return { state: 'ok' }
}

/**
 * Milliseconds until we should raise the dialog on our own, or null when there is nothing to schedule.
 * Waiting for a 401 would mean the message appears on the person's *next click* — possibly minutes
 * later, mid-gesture on a phone that has already stopped responding.
 */
export function msUntilExpiry(facts: SessionFacts, now: number): number | null {
  if (facts.source !== 'password' || facts.expiresAt === null) return null
  return Math.max(0, facts.expiresAt - EXPIRY_GRACE_MS - now)
}

//: Renew this long before the deadline. Long enough that a slow request still lands, short enough that
//: an abandoned tab is not kept alive by one stray renewal.
export const RENEW_WHEN_LEFT_MS = 120_000
//: Renew this often while someone works. The token lasts a day (owner, 2026-10-06: "час сесії до 1 доби",
//: "при активності сесія продовжується"), so renewing only near its end would let a day of work run out
//: on someone who stepped away for the last few minutes of it. Renewing every half hour of activity keeps
//: the deadline a day past the person's last activity; the server caps it at a week.
export const RENEW_EVERY_MS = 30 * 60_000
//: What counts as "still working". Deliberately generous: watching a phone without touching it is work,
//: and being thrown out mid-session is the thing we are fixing.
export const ACTIVE_WITHIN_MS = 5 * 60_000

/**
 * Should the workspace quietly extend the session right now?
 *
 * Keep someone signed in **while they are working**, and let an idle tab lapse. Both halves matter — a
 * session that dies mid-work is indistinguishable from a broken phone, and a session that renews forever
 * in a tab nobody is looking at defeats having a short token at all. The server adds a hard ceiling.
 */
export function shouldRenew(facts: SessionFacts, now: number): boolean {
  if (facts.source !== 'password' || facts.expiresAt === null || facts.refused) return false
  const left = facts.expiresAt - now
  // Already gone: renewal is for a live session, and the server refuses a dead token anyway.
  if (left <= 0) return false
  const due = left <= RENEW_WHEN_LEFT_MS || (facts.issuedAt !== undefined && now - facts.issuedAt >= RENEW_EVERY_MS)
  if (!due) return false
  const active = facts.lastActivityAt ?? 0
  return now - active <= ACTIVE_WITHIN_MS
}
